import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2 } from 'lucide-react';

import { Button } from '@/components/common/Button';
import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';
import type { Seat } from '@/lib/qcp/protocol';
import { qcpStatusOf, type QcpInvitePreview } from '@/lib/qcp/rest';
import { limuloApi } from '@/services/limuloApi';
import { useAppDispatch } from '@/store/hooks';
import { OSCURA } from '@/lib/v3';

interface CpAcceptInviteSheetProps {
  /**
   * Called with the match + the ACCEPTOR's seat once accept succeeds.
   * The acceptor is always seat B (the creator is A).
   */
  onAccepted: (result: { matchId: string; seat: Seat }) => void;
  onClose: () => void;
}

/**
 * Normalise a raw code input to the invite alphabet (A-Z, 2-9), max 10 chars.
 * Strips O/0/1/I which are excluded from the server's alphabet to avoid
 * visual ambiguity (same policy as `normaliseCode` in AcceptInviteSheet).
 */
function normaliseCode(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z2-9]/g, '')
    .slice(0, 10);
}

/**
 * Accept-by-code sheet for Chinese Poker (OFC).
 *
 * Flow: paste 10-char code → `invite.read` preview of the Limulo BFF (shows creator displayName + elo) → Accept (`invite.accept`)
 * → navigate to `/chinesepoker/match/:matchId` with seat B context. T1b: through the BFF, on the session of Limulo, no Bearer for QCP.
 *
 * Error mapping (BY STATUS: the BFF flattens every 4xx into `E_QLASH_HTTP_<n>` and does not carry the codes of the app):
 *   404              → "expired or already used"
 *   anything else    → generic failed (the old `cannot_accept_own` is no longer told apart from any other 400)
 */
export function CpAcceptInviteSheet({ onAccepted, onClose }: CpAcceptInviteSheetProps) {
  useLivelloIndietro(true, onClose);
  const { t } = useTranslation('chinesepoker');

  const [code, setCode] = useState('');
  const dispatch = useAppDispatch();
  const [preview, setPreview] = useState<QcpInvitePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<'expired' | 'failed' | null>(null);

  async function lookup() {
    if (code.length !== 10) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    const richiesta = dispatch(limuloApi.endpoints.getChinesePokerInvite.initiate(code, { forceRefetch: true }));
    try {
      const anteprima = await richiesta.unwrap();
      if (anteprima === null) throw new Error('unreadable');
      setPreview(anteprima);
    } catch (e) {
      setError(qcpStatusOf(e) === 404 ? 'expired' : 'failed');
    } finally {
      richiesta.unsubscribe();
      setLoading(false);
    }
  }

  async function accept() {
    setLoading(true);
    setError(null);
    const richiesta = dispatch(limuloApi.endpoints.acceptChinesePokerInvite.initiate(code));
    try {
      const esito = await richiesta.unwrap();
      if (esito === null) throw new Error('unreadable');
      onAccepted({ matchId: esito.matchId, seat: esito.seat });
    } catch (e) {
      setError(qcpStatusOf(e) === 404 ? 'expired' : 'failed');
    } finally {
      richiesta.reset();
      setLoading(false);
    }
  }

  const errorText =
    error === 'expired'
      ? t('invite.expired')
      : error
        ? t('invite.acceptError')
        : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={t('invite.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-bg-secondary p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-2">
          <h2 className="flex items-center gap-2 text-base font-bold text-fg">
            <KeyRound className="h-4 w-4 text-brand" />
            {t('invite.haveCode')}
          </h2>
        </div>

        <div className="flex flex-col gap-4 py-2">
          <input
            {...OSCURA}
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={code}
            onChange={(e) => {
              setCode(normaliseCode(e.target.value));
              setPreview(null);
              setError(null);
            }}
            placeholder="XXXXXXXXXX"
            maxLength={10}
            className="w-full rounded-xl border border-border bg-bg px-4 py-3 text-center text-xl font-bold tracking-[0.25em] text-fg placeholder:text-fg-muted placeholder:tracking-[0.25em]"
          />

          {errorText && <p className="text-sm text-danger">{errorText}</p>}

          {preview && (
            <p className="text-center text-sm text-fg">
              {t('invite.preview', {
                name: preview.creator.displayName,
                elo: preview.creator.elo,
              })}
            </p>
          )}

          {preview ? (
            <Button peso="bold" fullWidth onClick={accept} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('invite.accept')}
            </Button>
          ) : (
            <Button peso="bold" fullWidth onClick={lookup} disabled={code.length !== 10 || loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('invite.lookup')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
